// Tooltip (DESIGN_SPEC §10.16): raised, rule-strong hairline, no arrow, 6px offset, max 46ch.
//   <Tooltip lead? text? open?>{single trigger element}</Tooltip>
//       lead: the value, mono 500 · text: an Inter Tight explanation · plain strings only (rendered as text nodes)
//       opens after 700 ms on hover (instantly within 300 ms of another closing), immediately on keyboard focus; Esc, blur,
//       pointer-out and scroll close it. `open` forces it (true) or suppresses it (false). Never interactive, never the only home of data.
//   <Term tip>disposable guest</Term>   the dotted glossary term, focusable, with its tooltip
import { cloneElement, useEffect, useId, useLayoutEffect, useRef, useState, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";

type Phase = "closed" | "open" | "closing";
type Trigger = ReactElement<{ "aria-describedby"?: string }>;

let lastClosed = 0;

export function Tooltip({ lead, text, open, children }: { lead?: string; text?: string; open?: boolean; children: Trigger }) {
  const id = useId();
  const anchor = useRef<HTMLSpanElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const timer = useRef(0);
  const [phase, setPhaseState] = useState<Phase>("closed");
  const phaseRef = useRef<Phase>("closed");
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  const setPhase = (p: Phase) => {
    phaseRef.current = p;
    setPhaseState(p);
  };
  const show = (instant: boolean) => {
    if (open === false) return;
    window.clearTimeout(timer.current);
    if (instant || Date.now() - lastClosed < 300) setPhase("open");
    else timer.current = window.setTimeout(() => setPhase("open"), 700);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    if (phaseRef.current !== "open") return;
    lastClosed = Date.now();
    setPhase("closing");
  };

  const shown = open === true ? "open" : open === false ? "closed" : phase;

  useEffect(() => () => window.clearTimeout(timer.current), []);

  useEffect(() => {
    if (phase !== "closing") return;
    const t = window.setTimeout(() => setPhase("closed"), 120);
    return () => window.clearTimeout(t);
  }, [phase]);

  useEffect(() => {
    if (shown !== "open" || open === true) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && hide();
    const onScroll = () => hide();
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  });

  useLayoutEffect(() => {
    if (shown === "closed") return;
    let live = true;
    const measure = () => {
      const a = anchor.current?.firstElementChild ?? anchor.current;
      const t = tip.current;
      if (!live || !a || !t) return;
      const r = a.getBoundingClientRect();
      const w = t.offsetWidth;
      const h = t.offsetHeight;
      const vw = document.documentElement.clientWidth;
      // Flip above only when the anchor is on screen and the room below is short.
      const onScreen = r.bottom > 0 && r.top < window.innerHeight;
      const flip = onScreen && r.bottom + 6 + h > window.innerHeight - 8 && r.top - 6 - h > 8;
      const top = (flip ? r.top - 6 - h : r.bottom + 6) + window.scrollY;
      const left = Math.max(8, Math.min(r.left, vw - w - 8)) + window.scrollX;
      setPos((p) => (p && p.left === left && p.top === top ? p : { left, top }));
    };
    measure();
    void document.fonts?.ready.then(measure);
    window.addEventListener("resize", measure);
    // Late reflow (web fonts swapping in, content above growing) moves the anchor: follow it.
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    ro?.observe(document.body);
    if (anchor.current?.firstElementChild) ro?.observe(anchor.current.firstElementChild);
    return () => {
      live = false;
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, [shown, lead, text]);

  if (!lead && !text) return children;
  return (
    <>
      <span
        ref={anchor}
        className="contents"
        onMouseEnter={() => show(false)}
        onMouseLeave={hide}
        onFocus={(e) => e.target.matches(":focus-visible") && show(true)}
        onBlur={hide}
      >
        {cloneElement(children, { "aria-describedby": shown !== "closed" ? id : children.props["aria-describedby"] })}
      </span>
      {shown !== "closed" &&
        createPortal(
          <div
            ref={tip}
            id={id}
            role="tooltip"
            className={
              shown === "closing"
                ? "tip opacity-0 transition-opacity duration-120 ease-in"
                : "tip transition-[opacity,translate] duration-160 ease-out starting:translate-y-1 starting:opacity-0"
            }
            style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: 0 }}
          >
            {lead && <span className="v-lead">{lead}</span>}
            {lead && text && " "}
            {text && <span className="tip-text">{text}</span>}
          </div>,
          document.body,
        )}
    </>
  );
}

export function Term({ tip, children }: { tip: string; children: ReactNode }) {
  return (
    <Tooltip text={tip}>
      <span className="term" tabIndex={0}>
        {children}
      </span>
    </Tooltip>
  );
}
